
import axios from 'axios';
import fileDownload from 'js-file-download';
import HelperMixin from './HelperMixin';

export default {
  name: 'StudentEnrolmentTable',
  mixins: [HelperMixin],
  props: {
    apiUrl: {type: String, required: true},
    downloadUrl: {type: String, required: true},
  },
  data() {
    return {
      // filters and fields are filled via AJAX response
      filters: {},
      fields: [{key: 'select', label: ''}],
      showExmatriculated: false,
      enrolments: [],
      enrolmentsSelected: [],
      isLoading: false,
      isExporting: false,
      perPage: 250,
      currentPage: 1,
      showAllColumns: false,
      columnsAddedWhileFilterActive: [],
    };
  },
  computed: {
    filteredFields() {
      return this.fields.filter((field) => field.visible);
    },
    selectableFields() {
      // first three fields (select, mnr, name) cannot be hidden
      return this.fields.filter((field, index) => index > 2);
    },
    filteredEnrolments() {
      return this.enrolments.filter((enrolment) =>
        Object.keys(this.filters).every((key) =>
          this.matchesFilter(enrolment, key, this.filters[key]),
        ),
      );
    },
    totalRows() {
      return this.filteredEnrolments.length;
    },
    headsCount() {
      return this.filteredEnrolments
          .map((enrolment) => enrolment['s_id'])
          .filter((elem, pos, array) => array.indexOf(elem) === pos)
          .length;
    },
    combinedMailAddresses() {
      const collection = this.enrolmentsSelected.length === 0 ? this.filteredEnrolments : this.enrolmentsSelected;
      return collection
          .map((enrolment) => enrolment['s_email'])
          .filter((item, pos, self) => self.indexOf(item) === pos)
          .join('; ');
    },
  },
  watch: {
    'filteredFields'(newValue, oldValue) {
      const checkbox = $('#columnCheckbox');
      if (this.fields.find((field) => !field.visible) !== undefined) {
        this.showAllColumns = false;
        checkbox.prop('checked', false);
      } else {
        this.showAllColumns = true;
        checkbox.prop('checked', true);
      }
      window.localStorage.setItem('enrolmentTableShowAllColumns', this.showAllColumns);
      if (!this.showAllColumns) {
        const visibility = this.fields.reduce((result, column) => ({...result, [column.key]: column.visible}), {});
        window.localStorage.setItem('enrolmentTableColumnSettings', JSON.stringify(visibility));
      }
      // lock width for newly added columns
      if (newValue.length > oldValue.length) {
        this.lockColumnWidths();
        // store newly added columns if filters are set
        if (this.filteredEnrolments.length < this.enrolments.length) {
          const oldKeys = oldValue.map(field => field.key);
          this.columnsAddedWhileFilterActive = newValue.map(f => f.key).filter(key => !oldKeys.includes(key));
        }
      }
      // calculate offset values for non-overlapping sticky columns
      this.computeStickyColumnOffsets();
    },
    'filteredEnrolments'(newValue, oldValue) {
      this.computeStickyColumnOffsets();

      // if columns were added while filters were set and filter result gets broader
      if (this.columnsAddedWhileFilterActive.length && newValue.length > oldValue.length) {
        // reset width of stored columns so their width will be calculated again
        for (const key of this.columnsAddedWhileFilterActive) {
          $(`th.col-${key}`).width('');
        }
        this.lockColumnWidths();
        // reset stored columns if all filters were cleared
        if (newValue.length === this.enrolments.length) {
          this.columnsAddedWhileFilterActive = [];
        }
      }
    },
    'filters.s_email.value'() {
      this.dynamicColumnWidth('s_email');
    },
  },
  mounted() {
    this.fetchData();
    this.calculateContainerHeight();
    window.addEventListener('resize', this.calculateContainerHeight);
    window.addEventListener('resize', this.computeStickyColumnOffsets);
  },
  methods: {
    matchesFilter(enrolment, key, filter) {
      if (filter.type === 'string-contains') {
        if (filter.value === '') return true;
        if (filter.value === '---') {
          const val = enrolment[key];
          return val === null || val === undefined || val === '';
        }
        const field = this.fields.find((f) => f.key === key);
        if (field.filterByFormatted) {
          return String(field.formatter({value: enrolment[key]})).includes(filter.value.toString());
        }
        return String(enrolment[key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .includes(filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''));
      }
      if (filter.type === 'string-equals') {
        if (filter.value === '') return true;
        if (filter.value === '---') {
          const val = enrolment[key];
          return val === null || val === undefined || val === '';
        }
        const field = this.fields.find((f) => f.key === key);
        if (field.filterByFormatted) {
          return String(field.formatter({value: enrolment[key]})) === filter.value.toString();
        }
        return String(enrolment[key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '') ===
            filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
      }
      if (filter.type === 'multiselect') { // multiselect fields must not use filteredByFormatted option
        if (filter.value.length === 0) return true;
        return filter.value.some((selected) => {
          if (selected.id === '__empty__') {
            const val = enrolment[key];
            const containsEmptyValue = function (target) {
              if (Array.isArray(target)) return target.length === 0 || target.some(item => containsEmptyValue(item));
              else return target === null || target === undefined || target === '';
            }
            return containsEmptyValue(val);
          }

          const deepIncludes = function(target, value) { // recursive includes for arrays
            if (target === value) return true;
            if (Array.isArray(target)) {
              for (const elem of target) {
                if (deepIncludes(elem, value)) return true;
              }
            }
            return false;
          }

          return deepIncludes(enrolment[key], selected.id);
        });
      }
      return true;
    },
    fetchData() {
      this.isLoading = true;
      this.enrolmentsSelected = [];
      axios.get(this.apiUrl, {
        params: {
          showExmatriculated: this.showExmatriculated,
        },
      }).then((result) => {
        // first build a temporary array of all string names of all columns
        const columns = Object.keys(result.data.columns);

        // now build the filter spec for the table component
        const allFilterSpec = columns.reduce((spec, column) => {
          if (typeof result.data.columns[column].filter !== 'undefined') {
            spec[column] = result.data.columns[column].filter;
          }
          return spec;
        }, {});

        // now build the fields spec for the table component
        const allColumnsSpec = columns.map((column) => {
          const columnSpec = {
            key: column,
            label: result.data.columns[column].label,
            title: result.data.columns[column].title,
            sortable: result.data.columns[column].sortable,
            visible: result.data.columns[column].visible,
            class: 'col-' + column + ' table-b-table-default',
          };
          if (typeof result.data.columns[column].formatter !== 'undefined') {
            columnSpec['formatter'] = this[result.data.columns[column].formatter];
            if (typeof result.data.columns[column].sortByFormatted !== 'undefined') {
              columnSpec['sortByFormatted'] = result.data.columns[column].sortByFormatted;
            }
            if (typeof result.data.columns[column].filterByFormatted !== 'undefined') {
              columnSpec['filterByFormatted'] = result.data.columns[column].filterByFormatted;
            }
          }
          if (typeof result.data.columns[column].stickyColumn !== 'undefined') {
            columnSpec['stickyColumn'] = result.data.columns[column].stickyColumn;
          }
          if (typeof result.data.columns[column].class !== 'undefined') {
            columnSpec['class'] += ' ' + result.data.columns[column].class;
          }
          return columnSpec;
        });

        // add "---" option to all multiselect filters
        Object.values(allFilterSpec).forEach((filter) => {
          if (filter.type === 'multiselect') {
            filter.options = [{id: '__empty__', name: '---'}, ...(filter.options || [])];
            filter.dynamicOptions = filter.options;
          }
        });

        // finally update all data
        this.filters = allFilterSpec;
        this.fields = allColumnsSpec;
        this.enrolments = Object.freeze(result.data.enrolments);
        this.isLoading = false;

        // restore visible columns from saved settings
        const showAllColumns = JSON.parse(window.localStorage.getItem('enrolmentTableShowAllColumns'));
        const visibility = JSON.parse(window.localStorage.getItem('enrolmentTableColumnSettings'));
        if (!showAllColumns && visibility) {
          Object.keys(visibility).forEach((column) => {
            const field = this.fields.find((e) => e.key === column);
            // note that the field may currently be hidden, i.e. it must not be present in the table
            if (field) {
              field.visible = visibility[column];
            }
          });
        } else if (showAllColumns) {
          this.selectableFields.forEach((field, index, fields) => fields[index].visible = true);
        }
      }).catch(() => {
        this.isLoading = false;
      });
    },
    calculateDynamicFilterOptions(key) {
      const filteredOptionIds = new Set();
      const originalOptionIds = this.filters[key].options.map((opt) => opt.id);

      // if filter of field has no option selected, iterate over filtered enrolments, else over all
      for (const enrolment of (!this.filters[key].value.length && this.filteredEnrolments || this.enrolments)) {
        const cellValue = enrolment[key];
        const cellOptionIds = [];

        // get corresponding multiselect option(s) for current cell value
        const pushOption = function (value) {
          if (Array.isArray(value)) {
            if (value.length === 0) cellOptionIds.push('__empty__')
            else value.forEach(item => pushOption(item))
          } else if (value === null || value === undefined || value === '') cellOptionIds.push('__empty__')
          else cellOptionIds.push(value);
        }
        pushOption(cellValue);

        // check if new option(s) is/are already contained in filtered options
        const newOptionIds = cellOptionIds.filter(optId => !filteredOptionIds.has(optId));
        if (!newOptionIds.length) continue;

        // check if value would pass other filters without filter of current column
        if (this.filters[key].value.length) {
          const passesOthers = Object.keys(this.filters).every((otherKey) =>
              otherKey === key || this.matchesFilter(enrolment, otherKey, this.filters[otherKey]),
          );
          if (!passesOthers) continue;
        }

        // add new options to filtered options
        for (const optId of newOptionIds) {
          filteredOptionIds.add(optId);
        }

        // break if all options contained
        if (filteredOptionIds.size === originalOptionIds.length) {
          break;
        }
      }

      // set new dynamic options
      this.filters[key].dynamicOptions = this.filters[key].options.filter((opt) => filteredOptionIds.has(opt.id));
    },
    onRowSelected(rows) {
      this.enrolmentsSelected = rows;
    },
    toggleExmatriculated() {
      this.showExmatriculated = !this.showExmatriculated;
      this.fetchData();
    },
    toggleSelection(index) {
      if (this.$refs.table.selection.isActivated(index)) {
        this.$refs.table.selection.remove(index);
      } else {
        this.$refs.table.selection.add(index);
      }
    },
    resetMultiselect(filter) {
      filter.value = [];
    },
    downloadEnrolments() {
      let idList = [];
      this.isExporting = true;
      if (this.enrolmentsSelected.length === 0) {
        idList = this.filteredEnrolments.map((enrolment) => enrolment.id);
      } else {
        idList = this.enrolmentsSelected.map((enrolment) => enrolment.id);
      }
      axios({
        url: this.downloadUrl,
        method: 'POST',
        responseType: 'blob',
        data: {
          enrolments: idList,
          columns: this.filteredFields.map((field) => field.key),
        },
      }).then((response) => {
        fileDownload(response.data, 'Einschreibungen.xlsx');
        this.isExporting = false;
      }).catch(() => {
        this.isExporting = false;
      });
    },
    clearFilter(key) {
      this.filters[key].value = '';
    },
    clearFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key].value = '';
      });
    },
    calculateContainerHeight() {
      const table = $(this.$refs['table'].$el);
      const windowHeight = $(window).height();
      const offsetTop = table.offset().top;
      const footerHeight = $('footer').outerHeight();
      const tableHeight = windowHeight - offsetTop - footerHeight - 17;
      table.parents('.table-container').height(tableHeight);
    },
    multiselectLabelGenerator(item) {
      let label = item.name;
      if (item.short) {
        label += ' (' + item.short + ')';
      }
      return label;
    },
    resetVisibleFields() {
      const checkbox = $('#columnCheckbox');
      if (checkbox.prop('checked')) {
        this.selectableFields.forEach((field, index, fields) => fields[index].visible = true);
      } else {
        const visibility = JSON.parse(window.localStorage.getItem('enrolmentTableColumnSettings'));
        if (visibility) {
          Object.keys(visibility).forEach((column) => {
            const field = this.fields.find((e) => e.key === column);
            // note that the field may currently be hidden, i.e. it must not be present in the table
            if (field) {
              field.visible = visibility[column];
            }
          });
        }
      }
    },
    getLabel(key) {
      return this.fields.find((field) => field.key === key).label;
    },
    computeStickyColumnOffsets() {
      // wait until tables cells have been rendered
      this.$nextTick(function() {
        let offset = 0;
        // get header cells
        $('th.non-stacking-sticky-column').each(function() {
          // set header css
          $(this).css({'left': offset + 'px', 'z-index': 5});
          // get corresponding td cells
          const className = $(this).attr('class').split(' ').filter((className) => (className.startsWith('col-')));
          const cells = $('td.'+className);
          // set td cells css 'position' : '-webkit-sticky'
          cells.css({'left': offset + 'px', 'z-index': 2, 'position': 'sticky'});
          cells.css('position', '-webkit-sticky');
          // increase offset
          offset += cells.outerWidth();
        });
      });
    },
    dynamicColumnWidth(key) { // for columns with dynamic width
      this.$nextTick(() => {
        const th = $('th.col-' + key).width('');
        const maxWidth = Math.max(th[0].scrollWidth, ...$('td.col-' + key).toArray().map((td) => td.scrollWidth));
        th.width(maxWidth);
      });
    },
    lockColumnWidths() { // set fixed widths on columns to prevent jumps on filtering
      this.$nextTick(function() {
        $('th').filter((_, th) => th.style.width === '').each(function () {
          const colClass = $(this).attr('class').split(' ').find((className) => (className.startsWith('col-')));
          const maxWidth = Math.max(this.scrollWidth, ...$(`td.${colClass}`).toArray().map((td) => td.scrollWidth));
          $(this).width(maxWidth);
        });
      });
    },
  },
};
